import { addDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

/** A selectable entry in the schedule dialog's Time control. `value` is a 24-hour `HH:mm` string. */
export interface TimeOption {
  value: string;
  label: string;
}

const MINUTES_IN_DAY = 24 * 60;
const INCREMENT_MINUTES = 15;

const pad = (n: number): string => String(n).padStart(2, '0');

/** `'13:45'` -> `'1:45 PM'`. Hand-rolled rather than locale-formatted so the list is stable. */
export function toTimeLabel(value: string): string {
  const [hours, minutes] = value.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';

  return `${hours % 12 || 12}:${pad(minutes)} ${period}`;
}

function buildTimeOptions(): TimeOption[] {
  const options: TimeOption[] = [];

  for (let minutes = 0; minutes < MINUTES_IN_DAY; minutes += INCREMENT_MINUTES) {
    const value = `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
    options.push({ value, label: toTimeLabel(value) });
  }

  return options;
}

/** The same list for every dialog - it never varies, and every caller shares this instance. */
const BASE_TIME_OPTIONS: readonly TimeOption[] = Object.freeze(buildTimeOptions());

/** Every 15-minute slot in a day, `'00:00'` through `'23:45'`. */
export function toTimeOptions(): readonly TimeOption[] {
  return BASE_TIME_OPTIONS;
}

/** `'HH:mm'` for the given instant as read in `timezone` - the same zone `save()` converts through. */
export function toZonedTimeString(date: Date, timezone: string): string {
  return formatInTimeZone(date, timezone, 'HH:mm');
}

/**
 * The default time for a new schedule: the next top of the hour in the user's effective timezone.
 * When that rolls past midnight the returned `date` is the following day, so callers must patch
 * both fields - patching the time alone would put the meeting ~24 hours early.
 */
export function toNextTopOfHour(now: Date, timezone: string): { time: string; date: Date } {
  const currentHour = Number(formatInTimeZone(now, timezone, 'HH'));
  const nextHour = currentHour + 1;

  return nextHour >= 24 ? { time: '00:00', date: addDays(now, 1) } : { time: `${pad(nextHour)}:00`, date: now };
}

/**
 * Calendar events don't have to start on a quarter hour. Rather than snapping - which would
 * silently schedule the meeting at a different time than the event - keep the exact time and give
 * the select an option that can render it.
 */
export function withExtraTimeOption(options: readonly TimeOption[], value: string): readonly TimeOption[] {
  if (options.some(option => option.value === value)) return options;

  const extra = { value, label: toTimeLabel(value) };
  const index = options.findIndex(option => option.value > value);

  return index === -1 ? [...options, extra] : [...options.slice(0, index), extra, ...options.slice(index)];
}
